import React, { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Passenger } from '../../../data/models/passenger.model';
import { ServicePlan } from '../../../data/models/service-plan.model';
import * as uiSlice from '../../../data/ui.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as reservationSlice from '../../../data/reservation.slice';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import * as providerSlice from '../../../data/provider.slice';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import reservationUtils from '../../../services/reservationUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { TripLegType } from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import TripLegForm from './TripLegForm';
import { TripLeg } from '../../../data/models/reservation.model';
import OverrideDialog from './OverrideDialog';
import TabPanel from '../../TabPanel';
import MemberForm from './MemberForm';
import { useIsPassengerPortalOnlyUser } from '../../../hooks/permissions/useIsPassengerPortalOnlyUser';
import { usePortalName } from '../../../hooks/usePortalName';

const PREFIX = 'TripLegDialog';

const classes = {
  content: `${PREFIX}-content`,
  loadingContainer: `${PREFIX}-loading-container`,
  errorContainer: `${PREFIX}-error-container`,
  errorActions: `${PREFIX}-error-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.loadingContainer}`]: {
    textAlign: 'center',
    minHeight: 200,
    minWidth: 444,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  [`& .${classes.errorContainer}`]: {
    width: 444,
  },
  [`& .${classes.errorActions}`]: {
    justifyContent: 'end',
    '& button': {
      flex: 'none',
      width: 144,
    },
  },
}));

interface TripLegDialogProps {
  sequence: number;
  reservationReferenceID: string;
  tripLeg?: TripLeg;
  tripType: TripLegType;
  open: boolean;
  passenger: Passenger;
  servicePlans: ServicePlan[];
  close: () => void;
}

export default function TripLegDialog({
  sequence,
  reservationReferenceID,
  tripLeg,
  tripType,
  open,
  passenger,
  servicePlans,
  close,
}: TripLegDialogProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const isPassengerPortalOnlyUser = useIsPassengerPortalOnlyUser();
  const isPortal = usePortalName() ? true : false;
  const [tabIndex, setTabIndex] = React.useState(0);

  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const passengerClientData = useAppSelector(
    clientSlice.selectPassengerClientData,
  );
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  const timeZones = useAppSelector(lookupSlice.selectTimeZones);
  const passengerNoteTypes = useAppSelector(
    lookupSlice.selectPassengerNoteTypes,
  );
  const driverNoteTypes = useAppSelector(lookupSlice.selectDriverNoteTypes);
  const alternateContactTypes = useAppSelector(
    lookupSlice.selectAlternateContactTypes,
  );

  const validations = useAppSelector(
    reservationSlice.selectReservationValidations,
  );

  const syncedTripLegs = useAppSelector(tripLegSlice.selectTripLegDetails);
  const loadingSyncedTripLegs = useAppSelector(
    tripLegSlice.selectLoadingTripLegDetails,
  );
  const internalNoteList = useAppSelector(providerSlice.selectInternalNoteList);
  const canEditTripLeg = useMemo(() => {
    return reservationUtils.canPoralUserEditTripLeg(
      syncedTripLegs[tripLeg?.tripLegReferenceID!],
      isPortal,
    );
  }, [syncedTripLegs]);

  const isSaving = useAppSelector(reservationSlice.selectSavingTripLeg);

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: reservationUtils.getDefaults(
      passenger,
      null,
      transportationAttributes,
      tripLeg,
    ),
  });
  const formHookFields = useFieldArray({
    control: formHook.control,
    name: 'MultiLegTrips' as any,
  });

  const [isOverrideable, setIsOverrideable] = React.useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = React.useState(false);
  const [isOverridden, setIsOverridden] = React.useState(false);
  const [validationMessages, setValidationMessages] = React.useState<
    {
      field: string;
      message: string;
    }[]
  >([]);
  const pickUpTime = formHook.watch('MultiLegTrips[0].PickupTime');
  const appointmentDate = formHook.watch('MultiLegTrips[0].AppointmentTime');
  const tripLegServiceDate = (pickUpTime || appointmentDate)?.toISODate();
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );

  const clearCustomErrors = () => {
    const fields = validationMessages.map((msg) => msg.field);
    formHook.clearErrors(fields);
  };

  const isFormValid = useMemo(() => {
    let basicValid =
      formHook.formState.isValid &&
      Object.keys(formHook.formState.errors).length === 0;

    const { MultiLegTrips } = formHook.getValues();
    MultiLegTrips?.forEach((leg: any) => {
      if (leg.AdditionalPassengers && leg.AdditionalPassengerCount < 1) {
        basicValid = false;
      }

      if (!leg.PickupAddress || !leg.DropoffAddress) {
        basicValid = false;
      }

      if (!leg.AttributeLevel1) {
        basicValid = false;
      }
      if (!leg.MobilityAttribute) {
        basicValid = false;
      }
    });

    return basicValid;
  }, [formHook.formState, formHook.getValues]);

  const canSaveAsDraft = useMemo(() => {
    let canSave: boolean = tripLeg?.state === 'draft';

    const data = formHook.getValues();
    data.MultiLegTrips?.forEach((leg: any) => {
      canSave = canSave && !!leg.TripPurpose;
      canSave = canSave && !!leg.AppointmentTime;
      canSave = canSave && !!(leg.PickupTime || leg.WillCall);
      canSave = canSave && !!leg.PickupAddress;
      canSave = canSave && !!leg.DropoffAddress;
    });

    return canSave;
  }, [formHook.getValues()]);

  React.useEffect(() => {
    if (tripLeg) {
      const legDefaults = reservationUtils.getLegDefaults(
        passenger,
        reservationReferenceID,
        tripLeg,
        transportationAttributes,
      );
      formHookFields.update(0, legDefaults);
      const memberDefaults = reservationUtils.getMemberDefaults(
        tripLeg,
        transportationAttributes,
      );
      setIsOverridden(tripLeg.overrides && tripLeg.overrides.length > 0);
      if (transportationAttributes) {
        const secondaryAttrs = reservationUtils.getSecondaryAttributeDefaults(
          transportationAttributes,
          tripLeg,
        );
        const oldVals = formHook.getValues();
        formHook.reset({ ...oldVals, ...memberDefaults, ...secondaryAttrs });
      }
    } else {
      formHook.reset(
        reservationUtils.getDefaults(passenger, null, transportationAttributes),
      );
    }
    formHook.setValue('TripType', tripType);
    formHook.setValue('Metadata', tripLeg?.metadata);
  }, [tripLeg, tripType, transportationAttributes]);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const subscription = formHook.watch((value, { name }) => {
      if (!open) return;
      if (!name) return;
      if (name.endsWith('Address')) return;

      const oldReservation = (tripLeg as any)?.reservation;
      const reservation = {
        ...reservationUtils.getPartialReservationFromData(
          passenger.clientBC,
          passenger.passengerUUID!,
          formHook.getValues(),
        ),
        reservationReferenceID: oldReservation?.reservationReferenceID,
        standingOrderUUID: oldReservation?.standingOrderUUID,
      };
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        dispatch(reservationSlice.validateReservationAsync(reservation));
      }, 500);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [open, formHook.watch]);

  React.useEffect(() => {
    clearCustomErrors();
    if (!isOverridden && validations?.errors?.length) {
      const messages = reservationUtils.getFieldErrors(
        t,
        tripType === TripLegType.RoundTrip,
        validations,
      );
      setValidationMessages(messages);
      setIsOverrideable(!!messages.length);
    }
  }, [validations, formHook]);

  React.useEffect(() => {
    validationMessages.forEach((msg) => {
      formHook.setError(msg.field, {
        type: 'custom',
        message: msg.message,
      });
    });
  }, [validationMessages]);

  React.useEffect(() => {
    if (saveSuccessMessage) {
      handleClose();
    }
  }, [saveSuccessMessage]);

  useEffect(() => {
    if (tripLeg) {
      dispatch(
        tripLegSlice.getTripLegDetailsAsync(tripLeg.tripLegReferenceID!),
      );
      // Also load internal notes for this trip leg
      dispatch(
        providerSlice.fetchInternalNoteAsync(tripLeg.tripLegReferenceID!),
      );
    }
  }, [tripLeg]);

  useEffect(() => {
    if (tripLeg?.tripLegReferenceID) {
      // Parse height/weight/oxygen/stairs from internal notes
      // Expected format: "Height: XX\", Weight: YY lbs, Oxygen: ZZ, Stairs: WW"
      const gurneyNote = internalNoteList?.find(
        (note: any) =>
          note.tripLegInternalNoteComment?.includes('Height:') ||
          note.tripLegInternalNoteComment?.includes('Weight:') ||
          note.tripLegInternalNoteComment?.includes('Oxygen:') ||
          note.tripLegInternalNoteComment?.includes('Stairs:'),
      );

      if (gurneyNote) {
        const comment = gurneyNote.tripLegInternalNoteComment;
        const heightMatch = comment.match(/Height:\s*(\d+)/);
        const weightMatch = comment.match(/Weight:\s*(\d+)/);
        const oxygenMatch = comment.match(/Oxygen:\s*([a-f0-9-]+)/i);
        const stairsMatch = comment.match(/Stairs:\s*([a-f0-9-]+)/i);

        if (heightMatch?.[1]) {
          formHook.setValue(`MultiLegTrips[0].height`, heightMatch[1]);
        }
        if (weightMatch?.[1]) {
          formHook.setValue(`MultiLegTrips[0].weight`, weightMatch[1]);
        }
        if (oxygenMatch?.[1]) {
          formHook.setValue(`MultiLegTrips[0].AttributeLevel4`, oxygenMatch[1]);
        }
        if (stairsMatch?.[1]) {
          formHook.setValue(`MultiLegTrips[0].AttributeLevel5`, stairsMatch[1]);
        }
      }
    }
  }, [internalNoteList, tripLeg?.tripLegReferenceID]);

  const handleClose = () => {
    setTabIndex(0);
    setIsOverridden(false);
    setIsOverrideable(false);
    // Clear validation state first to avoid a spurious validate during reset
    dispatch(reservationSlice.reset());
    formHook.reset();
    close();
  };

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const reverseTransportationAttributeMap = useAppSelector(
    lookupSlice.selectReverseTransportationAttributeMap,
  );
  const secondLevelAttribute = formHook.watch(
    'MultiLegTrips[0].AttributeLevel2',
  );
  const gurneyAttribute = useMemo(
    () => reverseTransportationAttributeMap['GURNEY'],
    [reverseTransportationAttributeMap],
  );
  const bariatricGurneyAttribute = useMemo(
    () => reverseTransportationAttributeMap['BARIATRIC_GURNEY'],
    [reverseTransportationAttributeMap],
  );
  const isGurney = useMemo(() => {
    return secondLevelAttribute === gurneyAttribute;
  }, [secondLevelAttribute, gurneyAttribute]);
  const isBariatricGurney = useMemo(() => {
    return secondLevelAttribute === bariatricGurneyAttribute;
  }, [secondLevelAttribute, bariatricGurneyAttribute]);

  const saveTrips = (data: any, saveAsDraft: boolean) => {
    if (tripLeg) {
      const reservation = reservationUtils.getReservationFromFormData(
        passenger.clientBC,
        passenger.passengerUUID!,
        data,
        [...(tripLeg.overrides || [])],
        undefined,
        isGurney || isBariatricGurney
          ? {
              gurneyTypeTransportationAttributeUUID:
                data.MultiLegTrips[0].AttributeLevel2,
              memberHeightInches: parseInt(data.MultiLegTrips[0].height),
              memberWeightLbs: parseInt(data.MultiLegTrips[0].weight),
              oxygenCapacityTransportationAttributeUUID:
                data.MultiLegTrips[0].AttributeLevel4 || undefined,
              stairsCountTransportationAttributeUUID:
                data.MultiLegTrips[0].AttributeLevel5 || undefined,
              waitAndReturn: false,
            }
          : undefined,
      );
      dispatch(
        reservationSlice.updateTripLegsAsync(reservation.tripLegs, saveAsDraft),
      );
    } else {
      const overrideInfo = data.OverrideFirstName
        ? {
            firstName: data.OverrideFirstName,
            lastName: data.OverrideLastName,
            reason: data.OverrideReason,
            servicePlanUUID: data.OverrideServicePlan,
          }
        : null;
      const newTripLeg = reservationUtils.getTripLegFromData(
        passenger.passengerUUID!,
        data,
        data.MultiLegTrips[0],
        sequence,
        [],
        overrideInfo,
      );
      const passengerPhone = reservationUtils.getPassengerPhone(passenger);
      newTripLeg.pickUpPhone = newTripLeg.pickUpPhone ?? passengerPhone;
      newTripLeg.dropOffPhone = newTripLeg.dropOffPhone ?? passengerPhone;
      dispatch(
        reservationSlice.addTripLegAsync(
          reservationReferenceID,
          newTripLeg,
          saveAsDraft,
        ),
      );
    }
  };

  const save = (data: any) => {
    saveTrips(data, false);
  };

  const saveAsDraft = () => {
    const data = formHook.getValues();
    saveTrips(data, true);
  };

  const handleOverride = () => {
    setOverrideDialogOpen(true);
  };

  const handleOverrideClose = (data: any | null) => {
    if (data) {
      setIsOverridden(true);
      formHook.setValue('AllowOverride', data.AllowOverride);
      formHook.setValue('OverrideFirstName', data.OverrideFirstName);
      formHook.setValue('OverrideLastName', data.OverrideLastName);
      formHook.setValue('OverrideReason', data.OverrideReason);
      formHook.setValue('OverrideServicePlan', data.ServicePlan);
      clearCustomErrors();
    }
    setOverrideDialogOpen(false);
  };

  const renderEditLegContent = () => {
    return (
      <>
        <DialogTitle>
          {tripLeg ? t('reservation.editTripLeg') : t('reservation.addTripLeg')}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Tabs
              indicatorColor="primary"
              textColor="primary"
              value={tabIndex}
              onChange={handleTabChange}>
              <Tab
                label={t('reservation.tripDetails')}
                data-testid="trip-tab"
              />
              <Tab
                label={t('reservation.memberDetails')}
                data-testid="member-tab"
              />
            </Tabs>
            <TabPanel value={tabIndex} index={0}>
              <Box className={classes.content}>
                <TripLegForm
                  clientBC={passenger.clientBC}
                  servicePlans={servicePlans}
                  isReservation={false}
                  tripType={tripType}
                  tripPurposes={passengerClientData.purposeCodes}
                  transportationAttributes={transportationAttributes}
                  timeZones={timeZones}
                  index={0}
                  formHook={formHook}
                  canDelete={false}
                  isPortalReservation={isPassengerPortalOnlyUser}
                  deleteLeg={() => null}
                  passengerAddresses={passenger.passengerAddresses}
                />
              </Box>
            </TabPanel>
            <TabPanel value={tabIndex} index={1}>
              <Box className={classes.content}>
                <MemberForm
                  transportationAttributes={transportationAttributes}
                  passengerNoteTypes={passengerNoteTypes}
                  driverNoteTypes={driverNoteTypes}
                  alternateContactTypes={alternateContactTypes}
                  formHook={formHook}
                  isPortalReservation={isPassengerPortalOnlyUser}
                />
              </Box>
            </TabPanel>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            disabled={!isFormValid}
            onClick={formHook.handleSubmit(save)}>
            {t('common.done')}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={isSaving}
            onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          {!isPortal && (
            <Button
              color="secondary"
              variant="contained"
              disabled={isSaving || !isOverrideable || isOverridden}
              onClick={() => handleOverride()}>
              {t('reservation.requestOverride')}
            </Button>
          )}
          {tripLeg?.state === TripLegStatus.Draft && (
            <Button
              color="secondary"
              variant="contained"
              disabled={isSaving || !canSaveAsDraft}
              onClick={saveAsDraft}>
              {t('reservation.saveAsDraft')}
            </Button>
          )}
        </DialogActions>
      </>
    );
  };

  const renderErrorContent = () => {
    return (
      <>
        <DialogTitle>{t('tripLegErrors.dialogHeader')}</DialogTitle>
        <DialogContent>
          <Box className={classes.errorContainer}>
            <p>{t('tripLegErrors.dialogTitle')}</p>
            <ul>
              <li>{t('tripLegErrors.time')}</li>
              <li>{t('tripLegErrors.provider')}</li>
              <li>{t('tripLegErrors.driver')}</li>
              <li>{t('tripLegErrors.offered')}</li>
            </ul>
            <p>
              {t('tripLegErrors.dialogFooter')} <b>{t('footer.phoneNumber')}</b>
            </p>
          </Box>
        </DialogContent>
        <DialogActions className={classes.errorActions}>
          <Button variant="contained" onClick={handleClose}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </>
    );
  };

  const renderLoadingContent = () => {
    return (
      <DialogContent>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </DialogContent>
    );
  };

  return (
    <RootDialog
      open={open}
      onClose={handleClose}
      fullWidth={canEditTripLeg ? true : false}
      maxWidth="md">
      {loadingSyncedTripLegs && renderLoadingContent()}
      {!loadingSyncedTripLegs && canEditTripLeg && renderEditLegContent()}
      {!loadingSyncedTripLegs && !canEditTripLeg && renderErrorContent()}
      <OverrideDialog
        open={overrideDialogOpen}
        servicePlans={overridableServicePlans}
        close={handleOverrideClose}
      />
    </RootDialog>
  );
}
